/* eslint-disable prettier/prettier */
import React from 'react'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { showToast } from './toastSlice'
import makeRequest from 'src/services/api'
import handleGeneralError from '../helpers/errorHandler'
import {
  fetchBannerConfigUrl,
  createOrUpdateBannerConfigUrl,
  fetchLoyaltyMasterConfigUrl,
  fetchLoyaltyConfigValuesUrl,
  editLoyaltyConfigValuesUrl,
} from 'src/services/urls'
import { fetchCountries } from './countrySlice'

const initialState = {
  loading: false,
  bannerConfig: { bannerSectionDetails: {} },
  loyaltyMasterConfig: { features: [], parameters: [] },
  loyaltyConfigValues: { features: {}, parameters: {} },
  countryMasterConfig: { features: [], parameters: [] },
  error: '',
}

// Fetch loyalty members list
export const fetchBannerConfig = createAsyncThunk(
  'configuration/fetchBannerConfig',
  async (options, { dispatch }) => {
    try {
      const countryId = options?.countryId

      const response = await makeRequest(fetchBannerConfigUrl(countryId))
      return {
        bannerCategoryInfo: response.data.data.bannerCategoryInfo,
        bannerSectionDetails: response.data.data.bannerSectionDetails,
      }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

// Create Banner config
export const createBannerConfig = createAsyncThunk(
  'configuration/createBannerConfig',
  async (data, { dispatch }) => {
    try {
      let body = {
        bannerSectionTitle: data.bannerSectionTitle,
        bannerCount: Number(data.bannerCount),
        multilingualBannerSectionTitle: data.multilingualBannerSectionTitle,
      }

      const response = await makeRequest(createOrUpdateBannerConfigUrl(), 'POST', body)

      const { displayMessage, status } = response.data
      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
      }
      dispatch(fetchBannerConfig({}))
      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

// Edit/Update Banner configuration
export const editBannerConfig = createAsyncThunk(
  'configuration/editBannerConfig',
  async (data, { dispatch }) => {
    try {
      let body = {
        bannerSectionTitle: data.bannerSectionTitle,
        bannerCount: Number(data.bannerCount),
        multilingualBannerSectionTitle: data.multilingualBannerSectionTitle,
      }
      const response = await makeRequest(createOrUpdateBannerConfigUrl(), 'PATCH', body)

      const { displayMessage, status } = response.data
      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
      }
      dispatch(fetchBannerConfig({}))
      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

// fetching loyalty master config
export const fetchLoyaltyMasterConfig = createAsyncThunk(
  'configuration/fetchLoyaltyMasterConfig',
  async (options, { dispatch }) => {
    try {
      const response1 = await makeRequest(fetchLoyaltyMasterConfigUrl(options.type))
      const response2 = await makeRequest(fetchLoyaltyConfigValuesUrl(options.type))
      return {
        docs: response1?.data?.data?.docs[0],
        values: response2?.data?.data,
        newCountry: options?.newCountry || false,
      }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

// updating loyalty config
export const editLoyaltyConfig = createAsyncThunk(
  'configuration/editLoyaltyConfig',
  async (data, { dispatch }) => {
    try {
      let body = { ...data }

      const response = await makeRequest(editLoyaltyConfigValuesUrl(), 'PATCH', body)

      const { displayMessage, status } = response.data
      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
      }
      dispatch(fetchBannerConfig({}))
      dispatch(fetchCountries())
      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

const configurationSlice = createSlice({
  name: 'configuration',
  initialState,
  reducers: {
    // setActivePage: (state, action) => {
    //   state.activePage = action.payload
    // }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBannerConfig.pending, (state) => {
      state.loading = true
    })
    builder.addCase(fetchBannerConfig.fulfilled, (state, action) => {
      state.loading = false
      state.error = action.payload?.error
      if (!action.payload?.error) {
        state.bannerConfig = action.payload
      }
    })
    builder.addCase(fetchBannerConfig.rejected, (state, action) => {
      state.loading = false
      state.bannerConfig = {}
      state.error = action.error.message
    })
    builder.addCase(createBannerConfig.fulfilled, (state, action) => {
      // state.activePage = 0
      state.loading = false
      state.error = ''
    })
    builder.addCase(createBannerConfig.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    builder.addCase(editBannerConfig.fulfilled, (state, action) => {
      // state.activePage = 0
      state.loading = false
      state.error = ''
    })
    builder.addCase(editBannerConfig.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    builder.addCase(fetchLoyaltyMasterConfig.pending, (state) => {
      state.loading = true
    })
    builder.addCase(fetchLoyaltyMasterConfig.fulfilled, (state, action) => {
      state.loading = false
      state.error = action.payload?.error
      if (action.payload?.docs && action.payload?.values) {
        if (action.payload?.docs?.configurationType === 'country' && action.payload?.newCountry) {
          state.countryMasterConfig = action.payload?.docs
        } else {
          state.loyaltyMasterConfig = action.payload?.docs
          state.loyaltyConfigValues = action.payload?.values
        }
      }
    })
    builder.addCase(fetchLoyaltyMasterConfig.rejected, (state, action) => {
      state.loading = false
      state.loyaltyMasterConfig = { features: [], parameters: [] }
      state.error = action.error.message
    })
  },
})

export default configurationSlice.reducer
// export const { setActivePage } = configurationSlice.actions
